export interface MoneyFormatOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  display?: 'code' | 'symbol';
  currencySymbol?: string | null;
}

let symbols: Readonly<Record<string, string>> = Object.freeze({});
/** 启动拉取失败、按兜底规则展示符号；拿到正式表后立刻清掉。 */
let degraded = false;

/** 启动时安装后端符号快照；复制并冻结，防止调用方后续修改影响展示。 */
export function configureCurrencySymbols(
  value: Readonly<Record<string, string>>,
): void {
  symbols = Object.freeze({ ...value });
  degraded = false;
}

/**
 * 启动拉币种表失败时的兜底（网关免身份清单漏配、断网）。
 *
 * 规则与后端 `CurrencySymbols.all()` 一致：HKD 用 `HK$`，其余币种以代码本身作符号。
 * 只在这条降级路径上生效，正式表到达后就不再兜底——不能把「表里没有这个币种」
 * 也悄悄显示成代码，那个 null 是调用方要判断的缺失态。
 */
export function configureFallbackCurrencySymbols(): void {
  symbols = Object.freeze({});
  degraded = true;
}

/** 币种和符号都以正式契约为准，缺失时不从浏览器推断。 */
export function currencySymbol(
  currency: unknown,
  _display: 'code' | 'symbol' = 'code',
): string | null {
  if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) return null;
  if (Object.hasOwn(symbols, currency)) return symbols[currency];
  if (!degraded) return null;
  return currency === 'HKD' ? 'HK$' : currency;
}

/** 只格式化有效金额；空值不转成零，缺币种不补港币，调用方决定缺失态。 */
export function formatMoney(
  amount: unknown,
  currency: unknown,
  options: MoneyFormatOptions = {},
): string | null {
  if (
    (typeof amount !== 'number' && typeof amount !== 'string') ||
    (typeof amount === 'string' && !amount.trim())
  )
    return null;
  const value = Number(amount);
  const symbol = options.currencySymbol ?? currencySymbol(currency);
  if (
    !Number.isFinite(value) ||
    !symbol ||
    typeof currency !== 'string' ||
    !/^[A-Z]{3}$/.test(currency)
  )
    return null;
  const number = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(Math.abs(value));
  return `${value < 0 || Object.is(value, -0) ? '-' : ''}${symbol} ${number}`;
}
